/**
 * The ONE definition of the Sentry event scrubber.
 *
 * It lived in three byte-identical copies — the backend kernel and both SPAs —
 * until IMM-672.2. Copying a defensive utility copies its blind spots and then
 * hides them: each copy's tests passed, and a grep confirmed the pattern existed
 * on all three surfaces, while all three carried the same defect (BL-046).
 *
 * DELIBERATELY SDK-FREE. It imports neither `@sentry/node` nor `@sentry/react`
 * and takes a structurally-typed argument, which is what lets one module serve a
 * Node backend and two browser builds without pulling either SDK into the
 * other's bundle.
 */

/**
 * Attributes deleted outright, because they carry request content or the
 * query/fragment and nothing else — there is no diagnostic remainder to keep.
 *
 * Two naming schemes are folded into one list because a single pass visits
 * every attribute bag on the event: the OpenTelemetry / Sentry span attribute
 * names (`http.request.body.data`, `url.query`, `http.query`, and the
 * `http.fragment` the outgoing-request breadcrumb writes) and the bare keys
 * Sentry uses on `event.request` (`data`, `query_string`). No span attribute is
 * named bare `data` or `query_string`, so the union cannot delete something
 * worth keeping.
 *
 * `http.request.body.data` is NOT the same path as `event.request.data`:
 * `addNormalizedRequestDataToSpan` writes the former and
 * `addNormalizedRequestDataToEvent` the latter. A scrubber that deletes only
 * one leaves the reported leak (IMM-648) intact while reading as a fix.
 */
const DELETED_ATTRIBUTES: readonly string[] = [
  'http.request.body.data',
  'data',
  'url.query',
  'http.query',
  'url.fragment',
  'http.fragment',
  'query_string',
];

/**
 * Key segments that mean "this value is a URL".
 *
 * `referer` / `referrer` are here because a REQUEST HEADER carries a URL under
 * a name that says nothing about URLs, and headers reach Sentry twice — as
 * `event.request.headers` and as one `http.request.header.*` span attribute
 * each. On this product the referring page is a search results URL, so its
 * query string is the same disclosure as the request's own.
 */
const URL_BEARING_SEGMENTS = new Set(['url', 'uri', 'target', 'referer', 'referrer']);

/**
 * True for an attribute key that carries a URL — decided by SHAPE, not by
 * enumeration. That covers `url.full`, `url.path`, `http.url`, `http.target`,
 * the bare `url` on `event.request` and on an http breadcrumb, the `referer`
 * header under either spelling — and whatever URL attribute the next SDK
 * version invents, which is the point. An attribute nobody thought to list is
 * still truncated, because its NAME says it is a URL (BL-055).
 *
 * Header names are dash-delimited rather than dot-delimited, so both separators
 * are split on.
 */
function isUrlBearingAttribute(key: string): boolean {
  return key
    .toLowerCase()
    .split(/[.-]/)
    .some((segment) => URL_BEARING_SEGMENTS.has(segment));
}

/**
 * Cuts a URL at the first `?` or `#`, keeping scheme, host and path.
 *
 * Both separators, and whichever comes first: a fragment can appear without a
 * query, and truncating on `?` alone would leave `#…` — user-typed content of
 * exactly the class this function exists to remove.
 */
function truncateAtQueryOrFragment(value: string): string {
  const cut = value.search(/[?#]/);

  return cut === -1 ? value : value.slice(0, cut);
}

function isAttributeBag(candidate: unknown): candidate is Record<string, unknown> {
  return typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate);
}

function toArray(candidate: unknown): unknown[] {
  return Array.isArray(candidate) ? candidate : [];
}

/**
 * Every attribute bag on an outgoing event.
 *
 * The ROOT span is the one this list exists for. On a transaction its
 * attributes live in `event.contexts.trace.data`, NOT in `event.spans` — and
 * the root span is the one span that always carries the request URL. IMM-648's
 * scrubber iterated `event.spans` alone, so its `url.full` truncation never ran
 * on the only span that needed it (IMM-672).
 *
 * `event.contexts` is walked generically rather than reaching for `trace`:
 * every context carrying a `data` record is scrubbed, so a context the SDK adds
 * later is covered without an edit here.
 */
function collectAttributeBags(event: ScrubbableEvent): Record<string, unknown>[] {
  const bags: Record<string, unknown>[] = [];

  const add = (candidate: unknown): void => {
    if (isAttributeBag(candidate)) bags.push(candidate);
  };

  add(event.request);

  // Request headers are an attribute bag of their own, nested one level below
  // `event.request` — so the top-level pass walks straight past them. `Referer`
  // rides out here with the full query attached.
  add(isAttributeBag(event.request) ? event.request['headers'] : undefined);

  for (const context of Object.values(isAttributeBag(event.contexts) ? event.contexts : {})) {
    add(isAttributeBag(context) ? context['data'] : undefined);
  }

  for (const span of toArray(event.spans)) add(isAttributeBag(span) ? span['data'] : undefined);

  // Outgoing-request breadcrumbs carry `http.query` and a `url` of their own —
  // the emission path the IMM-672 wire capture turned up beyond the six fields
  // the report listed.
  for (const breadcrumb of toArray(event.breadcrumbs)) {
    add(isAttributeBag(breadcrumb) ? breadcrumb['data'] : undefined);
  }

  return bags;
}

function scrubAttributeBag(bag: Record<string, unknown>): void {
  for (const attribute of DELETED_ATTRIBUTES) delete bag[attribute];

  for (const [key, value] of Object.entries(bag)) {
    if (!isUrlBearingAttribute(key)) continue;

    // A header attribute's value is a string or an array of strings, depending
    // on whether the header was repeated — both carry the same URL.
    if (typeof value === 'string') bag[key] = truncateAtQueryOrFragment(value);
    else if (Array.isArray(value)) {
      bag[key] = value.map((entry) => (typeof entry === 'string' ? truncateAtQueryOrFragment(entry) : entry));
    }
  }
}

export type ScrubbableEvent = {
  request?: unknown;
  spans?: unknown[];
  breadcrumbs?: unknown[];
  contexts?: unknown;
};

/**
 * Strips request content — the body, the query string and the fragment — off an
 * outgoing Sentry event, whether it is an error event or a transaction.
 *
 * This is the belt-and-braces half of the IMM-648 fix: on the backend the
 * `requestDataIntegration` override already stops the SDK attaching the body,
 * but that override depends on `@sentry/core` continuing to spread
 * `options.include` last. The failure mode is silent and the consequence is
 * customer record content reaching a third-party processor, so the same fields
 * are deleted again here — an SDK upgrade that changes the defaults cannot
 * reopen the leak quietly. The browser SDK exports no such integration at all,
 * so for both SPAs this function is the whole guarantee.
 *
 * URL attributes are TRUNCATED at the first `?`, never removed: `include.url`
 * is hardcoded `true` in the SDK and the normalised URL carries the query
 * string, so excluding `query_string` alone still ships search terms inside
 * `url.full`. Scheme, host and path are kept — the path is the single most
 * diagnostically valuable attribute on a transaction, and `http.route` does not
 * carry path parameters.
 *
 * Total and never throws: ADR 0002 holds that telemetry never changes
 * application behaviour, and that applies to its own scrubber. Always returns
 * the event — dropping a transaction would be a behaviour change, not a scrub.
 */
export function scrubRequestContent<T extends ScrubbableEvent>(event: T): T {
  let bags: Record<string, unknown>[] = [];

  try {
    bags = collectAttributeBags(event);
  } catch {
    // See below. Collection walks a foreign object graph, so it is the one step
    // that can fail before any bag has been reached.
  }

  // Guarded per bag, not once around the loop: one bag with a hostile getter
  // must not take the remaining bags' scrub down with it, which is precisely
  // the "half-scrubbed beats not scrubbed" trade this catch exists to make.
  for (const bag of bags) {
    try {
      scrubAttributeBag(bag);
    } catch {
      // Deliberately swallowed (ADR 0002). A scrubber that throws inside
      // `beforeSendTransaction` takes the SDK's send path down with it, which
      // is telemetry changing application behaviour. The shape rules above are
      // total over every value the SDK can produce, so reaching here means the
      // event shape itself is malformed.
    }
  }

  return event;
}
